import React, { useEffect, useMemo, useState } from 'react';
import { FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import {
  filterResetPasswordUsersBySearch,
  getResetPasswordUserOptions,
  getTenantDisplayName,
  resolveTenantItsmUrl,
  TENANT_ITSM_URL_UNAVAILABLE_MESSAGE,
} from '../tenantHelpers';

const ResetTenantPasswordsModal = ({ tenant, isOpen, onClose, onConfirm, isSaving }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [liamUsername, setLiamUsername] = useState('');
  const [liamPassword, setLiamPassword] = useState('');
  const [showLiamPassword, setShowLiamPassword] = useState(false);
  const [view, setView] = useState('form');
  const [errors, setErrors] = useState({});

  const resettableUsers = useMemo(() => getResetPasswordUserOptions(), []);
  const resettableUserNames = useMemo(
    () => resettableUsers.map((user) => user.name),
    [resettableUsers]
  );

  const visibleUsers = useMemo(
    () => filterResetPasswordUsersBySearch(resettableUsers, userSearch),
    [resettableUsers, userSearch]
  );

  const visibleUserNames = useMemo(
    () => visibleUsers.map((user) => user.name),
    [visibleUsers]
  );

  const selectedCount = selectedUsers.length;
  const allResettableSelected = resettableUserNames.length > 0
    && resettableUserNames.every((name) => selectedUsers.includes(name));
  const allVisibleSelected = visibleUserNames.length > 0
    && visibleUserNames.every((name) => selectedUsers.includes(name));

  const tenantItsmUrl = useMemo(() => resolveTenantItsmUrl(tenant), [tenant]);

  const canSubmit = useMemo(
    () => selectedCount > 0
      && Boolean(liamUsername.trim())
      && Boolean(liamPassword.trim())
      && Boolean(tenantItsmUrl),
    [selectedCount, liamUsername, liamPassword, tenantItsmUrl]
  );

  const clearSensitiveState = () => {
    setLiamPassword('');
    setShowLiamPassword(false);
  };

  const resetForm = () => {
    setSelectedUsers([]);
    setUserSearch('');
    setLiamUsername('');
    clearSensitiveState();
    setView('form');
    setErrors({});
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, tenant?.id]);

  const handleClose = () => {
    clearSensitiveState();
    onClose();
  };

  if (!isOpen || !tenant) return null;

  const toggleUser = (userName) => {
    setSelectedUsers((prev) => (
      prev.includes(userName) ? prev.filter((item) => item !== userName) : [...prev, userName]
    ));
    setErrors((prev) => ({ ...prev, users: '' }));
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedUsers((prev) => prev.filter((name) => !visibleUserNames.includes(name)));
    } else {
      setSelectedUsers((prev) => [...new Set([...prev, ...visibleUserNames])]);
    }
    setErrors((prev) => ({ ...prev, users: '' }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!selectedCount) nextErrors.users = 'Select at least one user.';
    if (!tenantItsmUrl) nextErrors.itsmUrl = TENANT_ITSM_URL_UNAVAILABLE_MESSAGE;
    if (!liamUsername.trim()) nextErrors.liamUsername = 'Liam username is required.';
    if (!liamPassword.trim()) nextErrors.liamPassword = 'Liam password is required.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleProceedToConfirm = () => {
    if (!validateForm()) return;
    setView('confirm');
  };

  const handleConfirmReset = () => {
    onConfirm(selectedUsers, {
      username: liamUsername.trim(),
      password: liamPassword,
    });
  };

  const renderForm = () => (
    <>
      <p className="stm-reset-pwd__info">
        Reset selected users to their predefined default passwords. Privileged Liam credentials are required to authorize the reset.
      </p>
      {!tenantItsmUrl && (
        <p className="stm-reset-pwd__error stm-reset-pwd__error--banner" role="alert">
          {TENANT_ITSM_URL_UNAVAILABLE_MESSAGE}
        </p>
      )}

      <section className="stm-reset-pwd__section">
        <div className="stm-reset-pwd__section-head">
          <h3>Users to reset</h3>
          <span className="stm-reset-pwd__count">
            {selectedCount} selected
          </span>
        </div>

        <label className="stm-reset-pwd__search">
          <span className="stm-reset-pwd__search-label">Search users</span>
          <input
            type="search"
            value={userSearch}
            onChange={(event) => setUserSearch(event.target.value)}
            placeholder="Search users..."
          />
        </label>

        <div className="stm-reset-pwd__users">
          <label className="stm-reset-pwd__user stm-reset-pwd__user--all">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAllVisible}
            />
            <span>Select all</span>
          </label>
          {visibleUsers.length ? (
            visibleUsers.map(({ name }) => (
              <label key={name} className="stm-reset-pwd__user">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(name)}
                  onChange={() => toggleUser(name)}
                />
                <span>{name}</span>
              </label>
            ))
          ) : (
            <p className="stm-reset-pwd__empty">No users match your search.</p>
          )}
        </div>
        {errors.users && <span className="stm-reset-pwd__error">{errors.users}</span>}
      </section>

      <section className="stm-reset-pwd__section">
        <h3>Authorization</h3>
        <p className="stm-reset-pwd__helper">
          Enter the privileged Liam credentials used to reset passwords for this tenant.
        </p>
        <label className="stm-reset-pwd__field">
          <span className="stm-reset-pwd__label">Liam username</span>
          <input
            type="text"
            value={liamUsername}
            onChange={(event) => {
              setLiamUsername(event.target.value);
              setErrors((prev) => ({ ...prev, liamUsername: '' }));
            }}
            autoComplete="username"
          />
          {errors.liamUsername && <span className="stm-reset-pwd__error">{errors.liamUsername}</span>}
        </label>
        <label className="stm-reset-pwd__field">
          <span className="stm-reset-pwd__label">Liam password</span>
          <div className="stm-reset-pwd__secret">
            <input
              type={showLiamPassword ? 'text' : 'password'}
              value={liamPassword}
              onChange={(event) => {
                setLiamPassword(event.target.value);
                setErrors((prev) => ({ ...prev, liamPassword: '' }));
              }}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="stm-icon-btn stm-icon-btn--ghost"
              onClick={() => setShowLiamPassword((prev) => !prev)}
              aria-label="Toggle Liam password visibility"
            >
              {showLiamPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {errors.liamPassword && <span className="stm-reset-pwd__error">{errors.liamPassword}</span>}
        </label>
      </section>
    </>
  );

  const renderConfirm = () => (
    <div className="stm-reset-pwd__confirm">
      <p className="stm-reset-pwd__confirm-title">
        Reset passwords for {allResettableSelected ? `all ${selectedCount}` : selectedCount} user{selectedCount === 1 ? '' : 's'}?
      </p>
      <p className="stm-reset-pwd__confirm-text">
        The selected users on {getTenantDisplayName(tenant)} will be reset to the predefined default password.
      </p>
      {!allResettableSelected && (
        <ul className="stm-reset-pwd__confirm-list">
          {selectedUsers.map((user) => (
            <li key={user}>{user}</li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="stm-modal-overlay" onClick={handleClose}>
      <div className="stm-modal stm-modal--reset-pwd" onClick={(event) => event.stopPropagation()}>
        <header className="stm-modal__header">
          <div>
            <h2>Reset shared tenant passwords</h2>
            <p>{getTenantDisplayName(tenant)}</p>
          </div>
          <button type="button" className="stm-icon-btn" onClick={handleClose} disabled={isSaving} aria-label="Close">
            <FaTimes />
          </button>
        </header>

        <div className="stm-modal__body stm-modal__body--reset-pwd">
          {view === 'form' ? renderForm() : renderConfirm()}
        </div>

        <footer className="stm-modal__footer">
          {view === 'confirm' ? (
            <>
              <button
                type="button"
                className="stm-btn stm-btn--secondary"
                onClick={() => setView('form')}
                disabled={isSaving}
              >
                Cancel
              </button>
              <div className="stm-modal__footer-spacer" />
              <button
                type="button"
                className="stm-btn stm-btn--primary"
                disabled={isSaving}
                onClick={handleConfirmReset}
              >
                {isSaving ? 'Resetting...' : 'Reset Passwords'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="stm-btn stm-btn--secondary" onClick={handleClose} disabled={isSaving}>
                Cancel
              </button>
              <div className="stm-modal__footer-spacer" />
              <button
                type="button"
                className="stm-btn stm-btn--primary"
                disabled={isSaving || !canSubmit}
                onClick={handleProceedToConfirm}
              >
                Reset Passwords
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ResetTenantPasswordsModal;
